import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {useRouter} from "next/router";
import {useUser} from "@/base/components/auth/UserProvider";
import dynamic from "next/dynamic";

type QueueItem = {
	channel: string;
	event: string;
	callback: (data: any) => void;
}

type Pusher = {
	subscribe: (channel: string) => any;
	unsubscribe: (channel: string) => void;
	connection: {
		bind: (event: string, callback: () => void) => void;
	};
	channels: {
		channels: {
			[channel: string]: any;
		}
	};
	connect: () => void;
	disconnect: () => void;
	bind: (event: string, callback: (data: any) => void) => void;
	unbind: (event: string) => void;
} & any;

type Unscribe = () => void
type OnEvent = (channel: string, event: string, callback: (data: any) => void) => Unscribe
type OnPersonalEvent = (event: string, callback: (data: any) => void) => Unscribe

type AuthHandler = (data: any, callback: (err: any, data: any) => void) => void

type Context = {
	onEvent: OnEvent;
	onPersonalEvent: OnPersonalEvent;
	pusher?: Pusher | null;
	isConnected: () => boolean;
	isUserConnected: () => boolean;
	authHandler?: AuthHandler;
	isBroken?: boolean;
}

const PusherContext = createContext({} as Context)

export const PusherProvider = ({children}: {
	children: React.ReactNode
}) => {
	const [pusher, setPusher] = useState<Pusher | null>(null);

	const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
	const connectionTries = useRef<number>(-1);

	const [pusherBroken, setPusherBroken] = useState(false);

	const router = useRouter();

	const UserContext = useUser();

	const isConnected = (): boolean => {
		return pusher != null;
	}

	const isUserConnected = (): boolean => {
		return UserContext.loggedIn && isConnected();
	}

	const onEvent: OnEvent = (channel, event, callback) => {
		// setQueueEvents([...queueEvents, {
		// 	channel,
		// 	event,
		// 	callback
		// }]);

		if (pusher)
		{
			console.log('[WS] Pusher bind ' + channel + ' ' + event);

			if (!pusher.channels.channels[channel])
			{
				const cInstance = pusher.subscribe(channel);

				cInstance.bind(event, callback);
			}
			else
			{
				const cInstance = pusher.channels.channels[channel];

				cInstance.bind(event, callback);
			}

			return () => {
				if (pusher)
				{
					if (pusher.channels.channels[channel])
					{
						const cInstance = pusher.channels.channels[channel];

						cInstance.unbind(event);
						console.log('[WS] Pusher unbind ' + channel + ' ' + event);
					}
				}
			}
		}
		else
		{
			console.log('[WS] Pusher not connected');
		}

		console.log('[WS] Pusher queue event: ' + channel + ' ' + event)

		return () => {
		}
	}

	const onPersonalEvent: OnPersonalEvent = (event, callback) => {
		if (UserContext.loggedIn && UserContext.user)
		{
			return onEvent('private-user.' + UserContext.user.id, event, callback);
		}

		console.warn('[WS] User not logged in');

		return () => {
			console.warn('[WS] User not logged in');
		}
	}

	useEffect(() => {
		if (pusher)
		{
			if (connectionTries.current == -1)
			{
				connectionTries.current = 0;
			}

			if (timeoutId.current)
			{
				clearTimeout(timeoutId.current);
				timeoutId.current = null;
			}

			console.log('[WS] Pusher changed');
			pusher.connection.bind('connected', () => {
				console.log('[WS] Pusher connected on ' + connectionTries.current + ' tries');
				setPusherBroken(false);

				connectionTries.current = -1;
				if (timeoutId.current)
				{
					clearTimeout(timeoutId.current);
					timeoutId.current = null;
				}
			});
			pusher.connection.bind('disconnected', () => {
				console.log('[WS] Pusher disconnected');

				if (connectionTries.current > 0)
				{
					setPusherBroken(true);
				}

				if (connectionTries.current < 10)
				{
					connectionTries.current++;
					timeoutId.current = setTimeout(() => {
						console.log('[WS] Pusher reconnecting');
						pusher.connect();
					}, 4000);
				}
			});
			pusher.connection.bind('connecting', () => {
				console.log(`[WS] Pusher connecting ${connectionTries.current > 0 ? 'retry ' + connectionTries.current : ''}`);
			});
			pusher.connection.bind('unavailable', () => {
				console.log('[WS] Pusher unavailable');
			});
			pusher.connection.bind('error', (e: any) => {
				console.log('[WS] Pusher error');
				console.warn(JSON.stringify(e));
			});

			const presenceChannel = process.env.NEXT_PUBLIC_PUSHER_PRESENCE_TYPE || undefined;

			if (presenceChannel)
			{
				console.debug('[WS] Presence channel: ' + presenceChannel);
				onEvent(`presence-public-${presenceChannel}`, 'admin-status', (data) => {
					console.debug(data);
				});
			}
		}

		return () => {
			if (pusher)
			{
				console.log('[WS] Pusher disconnected');
				pusher.disconnect();
			}
		}
	}, [pusher]);

	useEffect(() => {
		if (!UserContext.loggedIn && pusher)
		{
			if (pusher.channels.channels['private-user.' + UserContext.user?.id])
			{
				pusher.unsubscribe('private-user.' + UserContext.user?.id);
			}
		}

		if (UserContext.user && pusher)
		{
			console.log("[WS] User logged in, set pusher");
		}
	}, [UserContext.user]);

	const context: Context = {
		onEvent,
		pusher,
		onPersonalEvent,
		isConnected,
		isUserConnected,
		isBroken: pusherBroken,
		// authHandler,
	}

	const pusherIsEnabled = process.env.NEXT_PUBLIC_PUSHER_ENABLED;
	const PusherInstanceDynamic = dynamic(() => import('@/base/components/PusherInstance'), {});

	return <PusherContext.Provider value={context}>
		{children}
		{pusherIsEnabled && <PusherInstanceDynamic dependencies={[]} pusher={pusher} setPusher={setPusher} options={{}}/>}
	</PusherContext.Provider>
}

export const usePusher = () => {
	return useContext(PusherContext);
}